import { Injectable } from '@angular/core';
import { APP_CONSTANTS } from '../constants/app.constants';

export interface PasswordStrengthResult {
  score: number; // 0-4 score, where 0 is very weak and 4 is very strong
  hasLowercase: boolean;
  hasUppercase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
  isLongEnough: boolean;
  feedback: string[];
}

@Injectable({
  providedIn: 'root',
})
export class PasswordStrengthService {
  private minLength = APP_CONSTANTS.VALIDATION.PASSWORD.MIN_LENGTH;
  private requireLowercase = APP_CONSTANTS.VALIDATION.PASSWORD.REQUIRE_LOWERCASE;
  private requireUppercase = APP_CONSTANTS.VALIDATION.PASSWORD.REQUIRE_UPPERCASE;
  private requireNumber = APP_CONSTANTS.VALIDATION.PASSWORD.REQUIRE_NUMBER;
  private requireSpecialChar = APP_CONSTANTS.VALIDATION.PASSWORD.REQUIRE_SPECIAL_CHAR;

  /**
   * Check password strength and return detailed results
   * @param password Password to check
   * @returns Password strength assessment object
   */
  checkStrength(password: string): PasswordStrengthResult {
    if (!password) {
      return this.getEmptyResult();
    }

    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[^A-Za-z0-9]/.test(password);
    const isLongEnough = password.length >= this.minLength;

    // Calculate score based on requirements
    let score = 0;
    const feedback: string[] = [];

    // Add points for meeting requirements
    if (isLongEnough) score += 1;
    if (hasLowercase) score += 1;
    if (hasUppercase) score += 1;
    if (hasNumber) score += 1;
    if (hasSpecialChar) score += 1;

    // Check for repeated patterns and adjust score
    if (this.hasRepeatedPatterns(password)) {
      score = Math.max(score - 1, 0);
      feedback.push('Avoid repeated patterns');
    }

    // Length bonus for very long passwords
    if (password.length > this.minLength * 1.5) {
      score = Math.min(score + 1, 4);
    }

    // Generate feedback
    if (!isLongEnough) {
      feedback.push(`Password must be at least ${this.minLength} characters`);
    }

    if (this.requireLowercase && !hasLowercase) {
      feedback.push('Include at least one lowercase letter');
    }

    if (this.requireUppercase && !hasUppercase) {
      feedback.push('Include at least one uppercase letter');
    }

    if (this.requireNumber && !hasNumber) {
      feedback.push('Include at least one number');
    }

    if (this.requireSpecialChar && !hasSpecialChar) {
      feedback.push('Include at least one special character');
    }

    return {
      score,
      hasLowercase,
      hasUppercase,
      hasNumber,
      hasSpecialChar,
      isLongEnough,
      feedback,
    };
  }

  /**
   * Check if password meets minimum security requirements
   * @param password Password to check
   * @returns True if password is secure enough
   */
  isSecureEnough(password: string): boolean {
    if (!password) return false;

    const result = this.checkStrength(password);

    // Check minimum requirements
    const meetsMinLength = !this.requireLowercase || result.isLongEnough;
    const meetsLowercase = !this.requireLowercase || result.hasLowercase;
    const meetsUppercase = !this.requireUppercase || result.hasUppercase;
    const meetsNumber = !this.requireNumber || result.hasNumber;
    const meetsSpecialChar = !this.requireSpecialChar || result.hasSpecialChar;

    return meetsMinLength && meetsLowercase && meetsUppercase && meetsNumber && meetsSpecialChar;
  }

  /**
   * Check if password contains repeated patterns
   * @param password Password to check
   * @returns True if repeated patterns are found
   */
  private hasRepeatedPatterns(password: string): boolean {
    // Check for repeated characters (3+ in a row)
    if (/(.)\1{2,}/.test(password)) {
      return true;
    }

    // Check for sequential characters (abc, 123, etc.)
    const sequences = [
      'abcdefghijklmnopqrstuvwxyz',
      '01234567890',
      'qwertyuiop',
      'asdfghjkl',
      'zxcvbnm',
    ];

    for (const seq of sequences) {
      for (let i = 0; i < seq.length - 2; i++) {
        const pattern = seq.substring(i, i + 3);
        if (password.toLowerCase().includes(pattern)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Get an empty result for when no password is provided
   * @returns Empty password strength result
   */
  private getEmptyResult(): PasswordStrengthResult {
    return {
      score: 0,
      hasLowercase: false,
      hasUppercase: false,
      hasNumber: false,
      hasSpecialChar: false,
      isLongEnough: false,
      feedback: ['Please enter a password'],
    };
  }
}
